import React from 'react';
import { Button, Card, List, Space, Tag, Typography, message } from 'antd';
import type { IncidentsProjection } from '../routes/execution/dashboardProjection';

const { Text } = Typography;

type Props = {
  incidents: IncidentsProjection;
  onResolve?: (incidentId: number) => Promise<void>;
  resolvingId?: number | null;
};

function formatTs(value: string): string {
  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return value;
  }
}

const SEVERITY_COLORS = { low: 'default', medium: 'orange', high: 'red' } as const;

export const ExecutionIncidentsPanel: React.FC<Props> = ({
  incidents,
  onResolve,
  resolvingId = null
}) => {
  if (incidents.items.length === 0) {
    return (
      <Card size="small" title="Инциденты">
        <Text type="secondary">Нет зарегистрированных проблем по маршруту.</Text>
      </Card>
    );
  }

  const handleResolve = async (id: number) => {
    if (!onResolve) return;
    try {
      await onResolve(id);
      message.success('Инцидент закрыт');
    } catch (e) {
      const err = e as { data?: { error?: string }; message?: string };
      message.error(err?.data?.error ?? err?.message ?? 'Не удалось закрыть инцидент');
    }
  };

  return (
    <Card size="small" title="Инциденты" style={{ marginBottom: 16 }}>
      <List
        size="small"
        dataSource={incidents.items}
        renderItem={(item) => (
          <List.Item
            actions={
              item.isActive && onResolve
                ? [
                    <Button
                      key="resolve"
                      size="small"
                      type="link"
                      loading={resolvingId === item.id}
                      onClick={() => void handleResolve(item.id)}
                    >
                      Закрыть
                    </Button>
                  ]
                : undefined
            }
          >
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Space wrap>
                <Tag color={item.isActive ? SEVERITY_COLORS[item.severity] : 'default'}>
                  {item.typeLabel}
                </Tag>
                {item.isActive ? <Tag color="red">Активен</Tag> : <Tag>Закрыт</Tag>}
                {item.affectsETA ? <Tag>ETA</Tag> : null}
                {item.financialImpact ? <Tag color="orange">Финансы</Tag> : null}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {formatTs(item.startedAt)}
                </Text>
              </Space>
              {item.comment ? <Text>{item.comment}</Text> : null}
            </Space>
          </List.Item>
        )}
      />
    </Card>
  );
};

export default ExecutionIncidentsPanel;
